import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { getFeatures } from '../../../util/APIUtils';
import { getActiveLocationId } from '../../services/utilty';
import { REENTRY_PLANNING_TABS } from './constants/reentry-planning.constants';
import {
  hasCasePlanAccess,
  isReentryPlanFeatureEnabled,
} from './utils/reentry-planning.util';

export const ACCESS_STATUS = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
};

export const useReentryPlanningModuleHook = () => {
  const [global] = useGlobal();
  const [accessStatus, setAccessStatus] = useState(ACCESS_STATUS.PENDING);
  const [activeTab, setActiveTab] = useState(REENTRY_PLANNING_TABS.ALL_PLANS);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());

  const tabs = useMemo(
    () => [
      {
        label: REENTRY_PLANNING_TABS.ALL_PLANS,
        value: REENTRY_PLANNING_TABS.ALL_PLANS,
        Icon: MeetingRoomIcon,
      },
    ],
    [],
  );

  const checkAccess = useCallback(async () => {
    if (!hasCasePlanAccess(global.permissions)) {
      setAccessStatus(ACCESS_STATUS.DENIED);
      return;
    }

    if (global.isSuperAdmin) {
      setAccessStatus(ACCESS_STATUS.GRANTED);
      return;
    }

    const locationId = getActiveLocationId();
    if (!locationId) {
      setAccessStatus(ACCESS_STATUS.DENIED);
      return;
    }

    try {
      const data = await getFeatures(locationId);
      if (data?.statusCode === 200 && isReentryPlanFeatureEnabled(data.locationFeatures)) {
        setAccessStatus(ACCESS_STATUS.GRANTED);
        return;
      }
      setAccessStatus(ACCESS_STATUS.DENIED);
    } catch {
      setAccessStatus(ACCESS_STATUS.DENIED);
    }
  }, [global.permissions, global.isSuperAdmin]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const onTabChange = useCallback((_event, value) => {
    setActiveTab(value);
  }, []);

  const refreshPlans = useCallback(() => {
    setLastRefreshed(new Date());
  }, []);

  return {
    accessStatus,
    activeTab,
    lastRefreshed,
    onTabChange,
    refreshPlans,
    tabs,
  };
};
